import { useState, useCallback } from 'react';
import { AddressSuggestion } from '@/types';

const MAX_RESULTS = 5;
const MIN_CHARS = 3;
const DEBOUNCE_DELAY = 300;

/**
 * Hook pour rechercher des adresses via l'API Gouv française
 * Gère les communes et adresses avec support des arrondissements
 */
export const useAddressApiGouv = () => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  let searchTimeout: NodeJS.Timeout | null = null;

  /**
   * Parse une requête de type "Ville + numéro" pour Paris/Marseille/Lyon
   * Ex: "Paris 2", "Marseille 5", "Lyon 3"
   */
  const parseCityArrondissementQuery = (query: string): { city: string; number: number } | null => {
    const match = query.trim().match(
      /^(Paris|Marseille|Lyon)\s*[-,\s]*(\d{1,2})(?:\s*(?:er|e|eme|ème|ᵉ|ᵉʳ)?)?(?:\s*(?:arr(?:\.|ondissement)?)?)?$/i
    );
    
    if (!match) return null;

    const city = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
    const num = parseInt(match[2], 10);
    const limits: Record<string, number> = { Paris: 20, Marseille: 16, Lyon: 9 };

    if (!limits[city] || num < 1 || num > limits[city]) return null;
    
    return { city, number: num };
  };

  /**
   * Génère le code INSEE d'un arrondissement à partir de la ville et du numéro
   */
  const arrondissementInseeFromCityAndNumber = (city: string, number: number): string | null => {
    if (city === 'Paris') return `751${number.toString().padStart(2, '0')}`;
    if (city === 'Marseille') return `132${number.toString().padStart(2, '0')}`;
    if (city === 'Lyon') return `6938${number}`;
    return null;
  };

  /**
   * Formate le nom d'un arrondissement
   */
  const formatArrondissementName = (city: string, number: number): string => {
    const suffix = number === 1 ? '1er' : `${number}e`;
    return `${city} ${suffix} Arrondissement`;
  };

  /**
   * Récupère une commune par code INSEE
   */
  const fetchCommuneByInsee = async (code: string): Promise<AddressSuggestion | null> => {
    try {
      const response = await fetch(
        `https://geo.api.gouv.fr/communes?code=${encodeURIComponent(code)}&fields=nom,code`
      );
      
      if (!response.ok) return null;
      
      const data = await response.json();
      if (!Array.isArray(data) || !data.length) return null;
      
      const commune = data[0];
      return {
        description: commune.nom,
        inseeCode: commune.code,
        type: 'commune',
      };
    } catch (error) {
      console.error('Error fetching commune by INSEE:', error);
      return null;
    }
  };

  /**
   * Recherche des communes via l'API Gouv
   */
  const searchCommunes = async (query: string): Promise<AddressSuggestion[]> => {
    try {
      const response = await fetch(
        `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(query)}&fields=nom,code,codesPostaux&limit=${MAX_RESULTS}`
      );
      
      if (!response.ok) return [];
      
      const data = await response.json();
      if (!Array.isArray(data)) return [];

      return data.map((commune: any) => ({
        description: commune.nom,
        inseeCode: commune.code,
        type: 'commune' as const,
      }));
    } catch (error) {
      console.error('Error searching communes:', error);
      return [];
    }
  };

  /**
   * Recherche des adresses via l'API Gouv
   */
  const searchAdresses = async (query: string): Promise<AddressSuggestion[]> => {
    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=${MAX_RESULTS}`
      );
      
      if (!response.ok) return [];
      
      const data = await response.json();
      if (!data.features || !Array.isArray(data.features)) return [];

      return data.features.map((feature: any) => ({
        description: feature.properties.label,
        inseeCode: feature.properties.citycode,
        type: 'adresse' as const,
      }));
    } catch (error) {
      console.error('Error searching addresses:', error);
      return [];
    }
  };

  /**
   * Fonction principale de recherche avec debounce
   */
  const searchAddress = useCallback((query: string) => {
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Reset if query too short
    if (query.length < MIN_CHARS) {
      setSuggestions([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    searchTimeout = setTimeout(async () => {
      try {
        const results: AddressSuggestion[] = [];

        // 1. Détection "Ville + numéro" pour arrondissements
        const parsedArr = parseCityArrondissementQuery(query);
        if (parsedArr) {
          const inseeCode = arrondissementInseeFromCityAndNumber(parsedArr.city, parsedArr.number);
          if (inseeCode) {
            const arrondissement = await fetchCommuneByInsee(inseeCode);
            if (arrondissement) {
              // Formatter le nom pour l'affichage
              arrondissement.description = formatArrondissementName(parsedArr.city, parsedArr.number);
              results.push(arrondissement);
            }
          }
        }

        // 2. Recherche standard communes et adresses en parallèle
        const [communes, adresses] = await Promise.all([
          searchCommunes(query),
          searchAdresses(query),
        ]);

        // 3. Fusionner les résultats (arrondissement d'abord si trouvé, puis communes, puis adresses)
        results.push(...communes, ...adresses);

        // 4. Limiter au maximum de résultats
        const limitedResults = results.slice(0, MAX_RESULTS);

        setSuggestions(limitedResults);
      } catch (err) {
        console.error('Search error:', err);
        setError('Erreur lors de la recherche d\'adresses');
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_DELAY);
  }, []);

  /**
   * Efface les suggestions
   */
  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setError(null);
  }, []);

  return {
    suggestions,
    loading,
    error,
    searchAddress,
    clearSuggestions,
  };
};
